/**
 * Section 09 — DailyBlast Strategy.
 *
 * In-house strategy using Heikin-Ashi smoothing + Linear-Regression
 * envelope (LR_HAMA Band). Designed for swing entries on the 1H–4H
 * timeframe; emits HIGH/MEDIUM/LOW signal-strength tags.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_strategy_dailyblast: GuideSection = {
  id: 'strategy-dailyblast',
  label: 'DailyBlast Strategy',
  group: 'strategies',
  icon: <BoltIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>DailyBlast Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Heikin-Ashi candles + Linear-Regression envelope. Swing-trade-friendly scanner targeted at 1H–4H
        intervals. Trades count: fewer than Sniper or RockBand but higher conviction per signal.
      </Typography>

      <GuideCallout kind="warning" title="Retired as an automated strategy">
        The DailyBlast <strong>polling / scanning engine is retired</strong> — it no longer runs scheduled
        scans, generates alerts, or places auto-trades. The only in-house engine driving autonomous trading
        today is RockBand (see the <strong>AutoTrade Lifecycle</strong> section). The DailyBlast
        Heikin-Ashi + LR-envelope <em>indicators</em> remain available as a manual chart overlay for
        analysis; the rules below describe that overlay's logic and are kept for reference.
      </GuideCallout>

      <H id="intuition">The intuition</H>
      <P>
        Regular candles flicker with every tick. Heikin-Ashi candles average OHLC across consecutive bars,
        producing a much smoother sequence — runs of same-colour HA candles strongly indicate trend.
        DailyBlast looks for the moment an HA candle <em>touches or breaches</em> a Linear-Regression
        envelope (drawn around a least-squares line of fit on the last N bars).
      </P>
      <P>
        Envelope touch = price reaching the statistical edge of recent range = either an entry-on-pullback
        signal (in trend) or a top/bottom reversal candidate (against trend). DailyBlast picks the trend
        side via the HA candle colour streak.
      </P>

      <H id="entry-rules">Entry rules</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Recompute LR envelope on the configured interval (default 1h, LinReg period 50, envelope offset 1× stdev).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Count consecutive same-colour HA candles (the "HA streak").</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>If HA streak is green (uptrend) AND last HA candle's low touches the lower envelope → <strong>BUY</strong> (pullback to support in uptrend).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>If HA streak is red (downtrend) AND last HA candle's high touches the upper envelope → <strong>SHORT</strong> (pullback to resistance in downtrend).</>} />
        </ListItem>
      </List>

      <H id="strength">Strength rules</H>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Streak length</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Strength</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>≥ 5 HA candles same colour</TableCell><TableCell><strong>HIGH</strong></TableCell></TableRow>
          <TableRow><TableCell>3–4 HA candles same colour</TableCell><TableCell><strong>MEDIUM</strong></TableCell></TableRow>
          <TableRow><TableCell>1–2 HA candles same colour</TableCell><TableCell><strong>LOW</strong> (logged only — does not auto-trade)</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        Only HIGH and MEDIUM are emitted as actionable alerts. LOW shows up in the Signal Log so you can
        see when DailyBlast is interested but not confident.
      </P>

      <H id="signal-payload">Signal payload</H>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Field</TableCell><TableCell sx={{ fontWeight: 700 }}>Value</TableCell></TableRow></TableHead>
        <TableBody>
          <TableRow><TableCell><code>action</code></TableCell><TableCell>BUY or SHORT.</TableCell></TableRow>
          <TableRow><TableCell><code>entryLevel</code></TableCell><TableCell>HA close of the touch candle.</TableCell></TableRow>
          <TableRow><TableCell><code>isl</code></TableCell><TableCell>The envelope edge on the opposite side (so SL is on the other side of the LinReg line).</TableCell></TableRow>
          <TableRow><TableCell><code>target</code></TableCell><TableCell>2.5× the ISL distance (DailyBlast targets are slightly bigger than Sniper/RockBand).</TableCell></TableRow>
          <TableRow><TableCell><code>strength</code></TableCell><TableCell>HIGH / MEDIUM / LOW per the table above.</TableCell></TableRow>
          <TableRow><TableCell><code>signalSource</code></TableCell><TableCell><code>DAILYBLAST</code></TableCell></TableRow>
        </TableBody>
      </Table>

      <H id="setup">Viewing the DailyBlast overlay (engine retired)</H>
      <P>
        DailyBlast can no longer be enabled as a scheduled-scan / auto-trade engine. Its Heikin-Ashi +
        LR-envelope <em>indicators</em> live on as a manual chart overlay:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Open a symbol on <OpenInApp to="/charts" label="Charts" /> and select <strong>DailyBlast</strong> from the strategy/overlay selector.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Best read on higher-liquidity instruments where HA smoothing produces clean streaks; 1h–4h intervals suit the swing framing.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Use it visually to spot trend (HA streak) + statistical-edge pullbacks (envelope touch); act via the manual BUY / SHORT capture if you choose to trade it. For autonomous trading, use RockBand.</>} />
        </ListItem>
      </List>

      <GuideExample title="A worked DailyBlast signal — TCS 1h, 11:00 IST">
        TCS has 6 consecutive green HA candles on the 1h, slowly grinding up from 3,820 to 3,870.<br />
        11:00 — Latest HA candle dips to a low of 3,855 — matches the lower-envelope value of LR(50,1σ).
        Streak HA-green=6 → HIGH strength. → BUY emitted.<br />
        Entry 3,860 (HA close), ISL 3,830 (upper-envelope distance below entry — wide stop), target 3,935
        (2.5× R).<br />
        14:00 — Price reaches 3,895. TSL activates.<br />
        Next day 10:30 — target hit at 3,935. +2.5R locked.
      </GuideExample>

      <GuideCallout kind="info" title="Historical note — DailyBlast's character">
        When it ran as an engine, DailyBlast emitted a handful of higher-conviction signals per day — wider
        stops, bigger targets, fewer-but-larger winners than the per-bar scanners. That automated engine is now
        retired; today only <strong>RockBand</strong> drives scheduled scans and auto-trade. The Heikin-Ashi +
        LR-envelope overlay remains useful as a manual read of trend + statistical-edge pullbacks on the chart.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'The intuition',   anchor: 'intuition',     body: 'intuition heikin ashi linear regression envelope swing' },
    { heading: 'Entry rules',     anchor: 'entry-rules',   body: 'entry rules ha streak envelope touch' },
    { heading: 'Strength rules',  anchor: 'strength',      body: 'strength high medium low ha streak' },
    { heading: 'Signal payload',  anchor: 'signal-payload',body: 'signal payload buy short entry isl target' },
    { heading: 'How to enable',   anchor: 'setup',         body: 'setup enable dailyblast watchlist interval' },
  ],
};

export default Section_strategy_dailyblast;
